import type { FC } from 'react';
import React from 'react';
import Pagination from '@mui/material/Pagination';
import type { PaginationRenderItemParams } from '@mui/material';
import { PaginationItem, Box } from '@mui/material';

interface CustomPaginationProps {
  id: string;
  count: number;
  page: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  siblingCount?: number;
  boundaryCount?: number;
  showFirstButton?: boolean;
  showLastButton?: boolean;
  disabled?: boolean;
  variant?: 'text' | 'outlined';
  shape?: 'circular' | 'rounded';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  color?: 'primary' | 'secondary' | 'standard';
  showPrevNextText?: boolean;
  hidePrevButton?: boolean;
  hideNextButton?: boolean;
}

interface CustomPaginationItemProps {
  item: PaginationRenderItemParams;
  PreviousComponent?: FC;
  NextComponent?: FC;
}

const PreviousComponent: FC = () => <span>Previous</span>;
const NextComponent: FC = () => <span>Next</span>;

const CustomPaginationItem: FC<CustomPaginationItemProps> = ({
  item,
  PreviousComponent,
  NextComponent,
}) => (
  <PaginationItem
    {...item}
    components={{
      previous: PreviousComponent,
      next: NextComponent,
    }}
  />
);

const CustomPagination: FC<CustomPaginationProps> = ({
  id,
  count,
  page,
  onPageChange,
  siblingCount = 1,
  boundaryCount = 1,
  showFirstButton = false,
  showLastButton = false,
  hidePrevButton = false,
  hideNextButton = false,
  disabled = false,
  variant = 'text',
  shape = 'rounded',
  size = 'medium',
  className = '',
  color = 'primary',
  showPrevNextText = false, // Default is false
}) => {
  return (
    <Box display="flex" justifyContent="center" className={className}>
      <Pagination
        id={id}
        count={count}
        page={page}
        onChange={onPageChange}
        siblingCount={siblingCount}
        boundaryCount={boundaryCount}
        showFirstButton={showFirstButton}
        showLastButton={showLastButton}
        hidePrevButton={hidePrevButton}
        hideNextButton={hideNextButton}
        disabled={disabled}
        variant={variant}
        shape={shape}
        size={size}
        color={color}
        renderItem={(item) => (
          <CustomPaginationItem
            item={item}
            PreviousComponent={showPrevNextText ? PreviousComponent : undefined}
            NextComponent={showPrevNextText ? NextComponent : undefined}
          />
        )}
      />
    </Box>
  );
};

export default CustomPagination;
